import React from "react"
import { TextField } from "@mui/material"
import { FormikProps } from "formik"
import i18n from "@/i18n"
import UserRolesEditor from "@/components/settings/user_roles_editor"

const STYLES = {
  container: "space-y-8",
  section: "",
  sectionHeading: "text-xl font-semibold mb-2 mt-0 pb-0 border-b border-gray-200",
  fieldsContainer: "space-y-4 mt-4",
  fieldRow: "grid grid-cols-2 gap-4",
  divider: "border-solid border-t-gray-300 border-t border-0",
} as const

interface UserFormFieldsProps {
  formik: FormikProps<any>
  showPasswordFields?: boolean
}

const UserFormFields: React.FC<UserFormFieldsProps> = ({ formik, showPasswordFields = false }) => {
  return (
    <div className={STYLES.container} data-testid="settings-users-form-container">
      <div className={STYLES.section}>
        <h3 className={STYLES.sectionHeading}>{i18n.t("app.settings.users.list.user_details")}</h3>

        <div className={STYLES.fieldsContainer}>
          <div className={STYLES.fieldRow}>
            <TextField
              label={i18n.t("app.settings.profile.overview.first_name")}
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={(formik.touched.firstName && formik.errors.firstName) as string}
              fullWidth
              required
              data-testid="settings-users-form-input-first-name"
            />
            <TextField
              label={i18n.t("app.settings.profile.overview.last_name")}
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={(formik.touched.lastName && formik.errors.lastName) as string}
              fullWidth
              required
              data-testid="settings-users-form-input-last-name"
            />
          </div>

          <TextField
            label={i18n.t("app.settings.profile.overview.email")}
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={(formik.touched.email && formik.errors.email) as string}
            fullWidth
            required
            data-testid="settings-users-form-input-email"
          />

          {showPasswordFields && (
            <div className={STYLES.fieldRow}>
              <TextField
                label={i18n.t("app.settings.users.list.password")}
                name="password"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={(formik.touched.password && formik.errors.password) as string}
                fullWidth
                required
                data-testid="settings-users-form-input-password"
              />
              <TextField
                label={i18n.t("app.settings.users.list.password_confirmation")}
                name="passwordConfirmation"
                type="password"
                value={formik.values.passwordConfirmation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.passwordConfirmation && Boolean(formik.errors.passwordConfirmation)}
                helperText={(formik.touched.passwordConfirmation && formik.errors.passwordConfirmation) as string}
                fullWidth
                required
                data-testid="settings-users-form-input-password-confirmation"
              />
            </div>
          )}

          <TextField
            label={i18n.t("app.settings.profile.overview.phone")}
            name="phone"
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={(formik.touched.phone && formik.errors.phone) as string}
            fullWidth
            data-testid="settings-users-form-input-phone"
          />

          <UserRolesEditor value={formik.values.roles} onChange={(roles) => formik.setFieldValue("roles", roles)} />

          <TextField
            label={i18n.t("app.settings.profile.overview.position")}
            name="position"
            value={formik.values.position}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.position && Boolean(formik.errors.position)}
            helperText={(formik.touched.position && formik.errors.position) as string}
            fullWidth
            data-testid="settings-users-form-input-position"
          />
        </div>
      </div>

      <div className={STYLES.divider} />

      <div className={STYLES.section}>
        <h3 className={STYLES.sectionHeading}>{i18n.t("app.settings.profile.overview.address_section")}</h3>

        <div className={STYLES.fieldsContainer}>
          <TextField
            label={i18n.t("app.settings.profile.overview.country")}
            name="country"
            value={formik.values.country}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.country && Boolean(formik.errors.country)}
            helperText={(formik.touched.country && formik.errors.country) as string}
            fullWidth
            data-testid="settings-users-form-input-country"
          />

          <TextField
            label={i18n.t("app.settings.profile.overview.address")}
            name="address"
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address && Boolean(formik.errors.address)}
            helperText={(formik.touched.address && formik.errors.address) as string}
            fullWidth
            data-testid="settings-users-form-input-address"
          />

          <div className={STYLES.fieldRow}>
            <TextField
              label={i18n.t("app.settings.profile.overview.city")}
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={(formik.touched.city && formik.errors.city) as string}
              fullWidth
              data-testid="settings-users-form-input-city"
            />
            <TextField
              label={i18n.t("app.settings.profile.overview.postcode")}
              name="postcode"
              value={formik.values.postcode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.postcode && Boolean(formik.errors.postcode)}
              helperText={(formik.touched.postcode && formik.errors.postcode) as string}
              fullWidth
              data-testid="settings-users-form-input-postcode"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserFormFields
